/**
 * Timezone detection and cookie setting utilities.
 * Provides browser timezone detection to help with server-side timezone handling.
 */

var _TIMEZONETOOLS_JS_ = true;

// Type helper for globalThis access
const _global = globalThis as Record<string, unknown>;

/**
 * Represents a timezone with its offset and daylight saving rules.
 */
interface Zone {
  id: string;
  hemisphere: string;
  offset: number;
  startRule: ZoneRule | null;
  endRule: ZoneRule | null;
}

/**
 * Represents a daylight saving time transition rule.
 */
interface ZoneRule {
  month: number;
  day: number;
  dayOfWeek: number;
  time: number;
  timeMode: number;
}

/**
 * Creates a Zone object.
 * @param id - Timezone identifier
 * @param offset - UTC offset in minutes
 * @param startRule - DST start rule (null if no DST)
 * @param endRule - DST end rule (null if no DST)
 */
function createZone(id: string, offset: number, startRule: ZoneRule | null, endRule: ZoneRule | null): Zone {
  return {
    id,
    hemisphere: startRule == null ? "-" : (startRule.month < endRule!.month ? "N" : "S"),
    offset,
    startRule,
    endRule
  };
}

/**
 * Creates a ZoneRule object for DST transitions.
 * @param month - Month (0-11)
 * @param day - Day of month or relative day
 * @param dayOfWeek - Day of week (0=Sunday)
 * @param time - Time of transition in minutes
 * @param timeMode - 0=wall time, 1=standard time, 2=UTC time
 */
function createZoneRule(month: number, day: number, dayOfWeek: number, time: number, timeMode: number): ZoneRule {
  return {
    month,
    day,
    dayOfWeek,
    time,
    timeMode
  };
}

// Module-level zones map
let ZONES: Record<string, Zone> | null = null;

/**
 * Adds a timezone to the ZONES map.
 * @param id - Primary timezone identifier
 * @param offset - UTC offset in seconds
 * @param dstOrAlias - Either DST offset in seconds, or first alias string
 * @param args - For DST zones: sMonth, sDay, sDoW, sTime, sTimeMode, eMonth, eDay, eDoW, eTime, eTimeMode, then aliases
 *               For non-DST zones: just alias strings
 */
function addZone(
  id: string,
  offset: number,
  dstOrAlias?: number | string,
  sMonth?: number,
  sDay?: number,
  sDoW?: number,
  sTime?: number,
  sTimeMode?: number,
  eMonth?: number,
  eDay?: number,
  eDoW?: number,
  eTime?: number,
  eTimeMode?: number,
  ...aliases: string[]
): void {
  let startRule: ZoneRule | null = null;
  let endRule: ZoneRule | null = null;
  let zone: Zone;
  let allAliases: string[];

  if (typeof dstOrAlias === "number") {
    // DST zone
    startRule = createZoneRule(sMonth!, sDay!, sDoW!, sTime! / 60, sTimeMode!);
    endRule = createZoneRule(eMonth!, eDay!, eDoW!, eTime! / 60, eTimeMode!);
    allAliases = aliases;
  } else {
    // Non-DST zone - dstOrAlias and remaining arguments are aliases
    allAliases = dstOrAlias !== undefined ? [dstOrAlias, sMonth as unknown as string, sDay as unknown as string, sDoW as unknown as string, sTime as unknown as string, sTimeMode as unknown as string, eMonth as unknown as string, eDay as unknown as string, eDoW as unknown as string, eTime as unknown as string, eTimeMode as unknown as string, ...aliases].filter(a => a !== undefined) as string[] : [];
  }

  zone = createZone(id, offset / 60, startRule, endRule);
  ZONES![id] = zone;

  for (const alias of allAliases) {
    if (alias !== undefined) {
      ZONES![alias] = zone;
    }
  }
}

/**
 * Initializes the ZONES map with all known timezones.
 */
function initZones(): void {
  ZONES = {};
  addZone("Etc/GMT+12", -43200);
  addZone("US/Samoa", -39600, "Etc/GMT+11", "MIT" as any, "Pacific/Apia" as any, "Pacific/Midway" as any, "Pacific/Niue" as any, "Pacific/Pago_Pago" as any, "Pacific/Samoa" as any);
  addZone("US/Hawaii", -36000, "Etc/GMT+10", "HST" as any, "Pacific/Fakaofo" as any, "Pacific/Honolulu" as any, "Pacific/Johnston" as any, "Pacific/Rarotonga" as any, "Pacific/Tahiti" as any, "SystemV/HST10" as any);
  addZone("US/Aleutian", -36000, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Adak", "America/Atka");
  addZone("Pacific/Marquesas", -34200);
  addZone("Pacific/Gambier", -32400, "Etc/GMT+9", "SystemV/YST9" as any);
  addZone("US/Alaska", -32400, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Anchorage", "America/Juneau", "America/Nome", "America/Yakutat", "AST", "SystemV/YST9YDT");
  addZone("Pacific/Pitcairn", -28800, "Etc/GMT+8", "SystemV/PST8" as any);
  addZone("US/Pacific", -28800, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Dawson", "America/Ensenada", "America/Los_Angeles", "America/Tijuana", "America/Vancouver", "America/Whitehorse", "Canada/Pacific", "Canada/Yukon", "Mexico/BajaNorte", "PST", "PST8PDT", "SystemV/PST8PDT", "US/Pacific-New");
  addZone("US/Arizona", -25200, "America/Dawson_Creek", "America/Hermosillo" as any, "America/Phoenix" as any, "Etc/GMT+7" as any, "PNT" as any, "SystemV/MST7" as any);
  addZone("US/Mountain", -25200, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Boise", "America/Cambridge_Bay", "America/Chihuahua", "America/Denver", "America/Edmonton", "America/Inuvik", "America/Mazatlan", "America/Shiprock", "America/Yellowknife", "Canada/Mountain", "Mexico/BajaSur", "MST", "MST7MDT", "Navajo", "SystemV/MST7MDT");
  addZone("America/Costa_Rica", -21600, "America/Belize", "America/El_Salvador" as any, "America/Guatemala" as any, "America/Managua" as any, "America/Regina" as any, "America/Swift_Current" as any, "America/Tegucigalpa" as any, "Canada/East-Saskatchewan" as any, "Canada/Saskatchewan" as any, "Etc/GMT+6" as any, "Pacific/Galapagos" as any, "SystemV/CST6" as any);
  addZone("US/Central", -21600, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Cancun", "America/Chicago", "America/Menominee", "America/Merida", "America/Mexico_City", "America/Monterrey", "America/North_Dakota/Center", "America/Rainy_River", "America/Rankin_Inlet", "CST", "CST6CDT", "Mexico/General", "SystemV/CST6CDT");
  addZone("America/Winnipeg", -21600, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 1, "Canada/Central");
  addZone("Pacific/Easter", -21600, 3600, 9, 9, 1, 14400, 2, 2, 9, 1, 10800, 2, "Chile/EasterIsland");
  addZone("US/East-Indiana", -18000, "America/Bogota", "America/Cayman" as any, "America/Eirunepe" as any, "America/Fort_Wayne" as any, "America/Guayaquil" as any, "America/Indiana/Indianapolis" as any, "America/Indiana/Knox" as any, "America/Indiana/Marengo" as any, "America/Indiana/Vevay" as any, "America/Indianapolis" as any, "America/Jamaica" as any, "America/Knox_IN" as any, "America/Lima" as any, "America/Panama" as any, "America/Port-au-Prince" as any, "America/Porto_Acre" as any, "America/Rio_Branco" as any, "Brazil/Acre" as any, "Etc/GMT+5" as any, "IET" as any, "Jamaica" as any, "SystemV/EST5" as any, "US/Indiana-Starke" as any);
  addZone("US/Eastern", -18000, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Detroit", "America/Iqaluit", "America/Kentucky/Louisville", "America/Kentucky/Monticello", "America/Louisville", "America/Montreal", "America/Nassau", "America/New_York", "America/Nipigon", "America/Pangnirtung", "America/Thunder_Bay", "America/Toronto", "Canada/Eastern", "EST", "EST5EDT", "SystemV/EST5EDT", "US/Michigan");
  addZone("America/Grand_Turk", -18000, 3600, 3, 1, 1, 0, 0, 9, -1, 1, 0, 0);
  addZone("America/Havana", -18000, 3600, 3, 1, 1, 0, 1, 9, -1, 1, 0, 1, "Cuba");
  addZone("America/Puerto_Rico", -14400, "America/Anguilla", "America/Antigua" as any, "America/Aruba" as any, "America/Barbados" as any, "America/Boa_Vista" as any, "America/Caracas" as any, "America/Cuiaba" as any, "America/Curacao" as any, "America/Dominica" as any, "America/Grenada" as any, "America/Guadeloupe" as any, "America/Guyana" as any, "America/La_Paz" as any, "America/Manaus" as any, "America/Martinique" as any, "America/Montserrat" as any, "America/Port_of_Spain" as any, "America/Porto_Velho" as any, "America/Santo_Domingo" as any, "America/St_Kitts" as any, "America/St_Lucia" as any, "America/St_Thomas" as any, "America/St_Vincent" as any, "America/Tortola" as any, "America/Virgin" as any, "Brazil/West" as any, "Etc/GMT+4" as any, "PRT" as any, "SystemV/AST4" as any);
  addZone("America/Halifax", -14400, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0, "America/Glace_Bay", "America/Thule", "Atlantic/Bermuda", "Canada/Atlantic", "SystemV/AST4ADT");
  addZone("America/Goose_Bay", -14400, 3600, 3, 1, 1, 60, 0, 9, -1, 1, 60, 0);
  addZone("America/Santiago", -14400, 3600, 9, 9, 1, 14400, 2, 2, 9, 1, 10800, 2, "Chile/Continental");
  addZone("America/Asuncion", -14400, 3600, 8, 1, 1, 0, 0, 3, 1, 1, 0, 0);
  addZone("Atlantic/Stanley", -14400, 3600, 8, 1, 1, 7200, 0, 3, 15, 1, 7200, 0);
  addZone("America/Campo_Grande", -14400, 3600, 9, 15, 1, 0, 0, 1, 15, 1, 0, 0);
  addZone("Antarctica/Palmer", -14400, 3600, 9, 9, 1, 0, 0, 2, 9, 1, 0, 0);
  addZone("Canada/Newfoundland", -12600, 3600, 3, 1, 1, 60, 0, 9, -1, 1, 60, 0, "America/St_Johns", "CNT");
  addZone("America/Buenos_Aires", -10800, "AGT", "America/Araguaina" as any, "America/Bahia" as any, "America/Belem" as any, "America/Catamarca" as any, "America/Cayenne" as any, "America/Cordoba" as any, "America/Fortaleza" as any, "America/Jujuy" as any, "America/Maceio" as any, "America/Mendoza" as any, "America/Montevideo" as any, "America/Paramaribo" as any, "America/Recife" as any, "America/Rosario" as any, "Antarctica/Rothera" as any, "Etc/GMT+3" as any);
  addZone("America/Godthab", -10800, 3600, 2, -1, 1, 3600, 2, 9, -1, 1, 3600, 2);
  addZone("America/Miquelon", -10800, 3600, 3, 1, 1, 7200, 0, 9, -1, 1, 7200, 0);
  addZone("Brazil/East", -10800, 3600, 9, 15, 1, 0, 0, 1, 15, 1, 0, 0, "America/Sao_Paulo", "BET");
  addZone("Atlantic/South_Georgia", -7200, "America/Noronha", "Brazil/DeNoronha" as any, "Etc/GMT+2" as any);
  addZone("Atlantic/Cape_Verde", -3600, "Etc/GMT+1");
  addZone("Atlantic/Azores", -3600, 3600, 2, -1, 1, 3600, 2, 9, -1, 1, 3600, 2, "America/Scoresbysund");
  addZone("GMT", 0, "Africa/Abidjan", "Africa/Accra" as any, "Africa/Bamako" as any, "Africa/Banjul" as any, "Africa/Bissau" as any, "Africa/Casablanca" as any, "Africa/Conakry" as any, "Africa/Dakar" as any, "Africa/El_Aaiun" as any, "Africa/Freetown" as any, "Africa/Lome" as any, "Africa/Monrovia" as any, "Africa/Nouakchott" as any, "Africa/Ouagadougou" as any, "Africa/Sao_Tome" as any, "Africa/Timbuktu" as any, "America/Danmarkshavn" as any, "Atlantic/Reykjavik" as any, "Atlantic/St_Helena" as any, "Etc/GMT" as any, "Etc/GMT+0" as any, "Etc/GMT-0" as any, "Etc/GMT0" as any, "Etc/Greenwich" as any, "Etc/UCT" as any, "Etc/Universal" as any, "Etc/UTC" as any, "Etc/Zulu" as any, "GMT0" as any, "Greenwich" as any, "Iceland" as any, "UCT" as any, "Universal" as any, "UTC" as any, "Zulu" as any);
  addZone("Europe/London", 0, 3600, 2, -1, 1, 3600, 2, 9, -1, 1, 3600, 2, "Atlantic/Canary", "Atlantic/Faeroe", "Atlantic/Madeira", "Eire", "Europe/Belfast", "Europe/Dublin", "Europe/Lisbon", "GB", "GB-Eire", "Portugal", "WET");
  addZone("Africa/Algiers", 3600, "Africa/Bangui", "Africa/Brazzaville" as any, "Africa/Douala" as any, "Africa/Kinshasa" as any, "Africa/Lagos" as any, "Africa/Libreville" as any, "Africa/Luanda" as any, "Africa/Malabo" as any, "Africa/Ndjamena" as any, "Africa/Niamey" as any, "Africa/Porto-Novo" as any, "Africa/Tunis" as any, "Etc/GMT-1" as any);
  addZone("Europe/Paris", 3600, 3600, 2, -1, 1, 3600, 2, 9, -1, 1, 3600, 2, "Africa/Ceuta", "Arctic/Longyearbyen", "Atlantic/Jan_Mayen", "ECT", "Europe/Amsterdam", "Europe/Andorra", "Europe/Belgrade", "Europe/Berlin", "Europe/Bratislava", "Europe/Brussels", "Europe/Budapest", "Europe/Copenhagen", "Europe/Gibraltar", "Europe/Ljubljana", "Europe/Luxembourg", "Europe/Madrid", "Europe/Malta", "Europe/Monaco", "Europe/Oslo", "Europe/Prague", "Europe/Rome", "Europe/San_Marino", "Europe/Sarajevo", "Europe/Skopje", "Europe/Stockholm", "Europe/Tirane", "Europe/Vaduz", "Europe/Vatican", "Europe/Vienna", "Europe/Warsaw", "Europe/Zagreb", "Europe/Zurich", "Poland");
  addZone("CET", 3600, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "MET");
  addZone("Africa/Windhoek", 3600, 3600, 8, 1, 1, 7200, 0, 3, 1, 1, 7200, 0);
  addZone("Africa/Harare", 7200, "Africa/Blantyre", "Africa/Bujumbura" as any, "Africa/Gaborone" as any, "Africa/Johannesburg" as any, "Africa/Kigali" as any, "Africa/Lubumbashi" as any, "Africa/Lusaka" as any, "Africa/Maputo" as any, "Africa/Maseru" as any, "Africa/Mbabane" as any, "Africa/Tripoli" as any, "CAT" as any, "Etc/GMT-2" as any, "Libya" as any);
  addZone("Europe/Istanbul", 7200, 3600, 2, -1, 1, 3600, 2, 9, -1, 1, 3600, 2, "Asia/Istanbul", "Asia/Nicosia", "EET", "Europe/Athens", "Europe/Bucharest", "Europe/Chisinau", "Europe/Helsinki", "Europe/Kiev", "Europe/Nicosia", "Europe/Riga", "Europe/Simferopol", "Europe/Sofia", "Europe/Tallinn", "Europe/Tiraspol", "Europe/Uzhgorod", "Europe/Vilnius", "Europe/Zaporozhye", "Turkey");
  addZone("Asia/Damascus", 7200, 3600, 3, 1, 0, 0, 0, 9, 1, 0, 0, 0);
  addZone("Asia/Jerusalem", 7200, 3600, 3, 1, 0, 3600, 0, 9, 1, 0, 3600, 0, "Asia/Tel_Aviv", "Israel");
  addZone("Asia/Beirut", 7200, 3600, 2, -1, 1, 0, 0, 9, -1, 1, 0, 0);
  addZone("Europe/Kaliningrad", 7200, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "Europe/Minsk");
  addZone("Asia/Amman", 7200, 3600, 2, -1, 5, 0, 1, 8, -1, 5, 0, 1);
  addZone("Africa/Cairo", 7200, 3600, 3, -1, 6, 0, 1, 8, -1, 5, 82800, 1, "ART", "Egypt");
  addZone("Asia/Gaza", 7200, 3600, 3, 15, 6, 0, 0, 9, 15, 6, 0, 0);
  addZone("Africa/Addis_Ababa", 10800, "Africa/Asmera", "Africa/Dar_es_Salaam" as any, "Africa/Djibouti" as any, "Africa/Kampala" as any, "Africa/Khartoum" as any, "Africa/Mogadishu" as any, "Africa/Nairobi" as any, "Antarctica/Syowa" as any, "Asia/Aden" as any, "Asia/Bahrain" as any, "Asia/Kuwait" as any, "Asia/Qatar" as any, "Asia/Riyadh" as any, "EAT" as any, "Etc/GMT-3" as any, "Indian/Antananarivo" as any, "Indian/Comoro" as any, "Indian/Mayotte" as any);
  addZone("Europe/Moscow", 10800, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "W-SU");
  addZone("Asia/Baghdad", 10800, 3600, 3, 1, 0, 10800, 1, 9, 1, 0, 10800, 1);
  addZone("Asia/Riyadh87", 11224, "Asia/Riyadh88", "Asia/Riyadh89" as any, "Mideast/Riyadh87" as any, "Mideast/Riyadh88" as any, "Mideast/Riyadh89" as any);
  addZone("Asia/Tehran", 12600, 3600, 2, 21, 0, 0, 0, 8, 21, 0, 0, 0, "Iran");
  addZone("Asia/Muscat", 14400, "Asia/Dubai", "Etc/GMT-4" as any, "Indian/Mahe" as any, "Indian/Mauritius" as any, "Indian/Reunion" as any);
  addZone("Asia/Yerevan", 14400, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "Asia/Aqtau", "Asia/Oral", "Europe/Samara", "NET");
  addZone("Asia/Tbilisi", 14400, 3600, 2, -1, 1, 0, 0, 9, -1, 1, 0, 0);
  addZone("Asia/Baku", 14400, 3600, 2, -1, 1, 3600, 0, 9, -1, 1, 3600, 0);
  addZone("Asia/Kabul", 16200);
  addZone("Asia/Karachi", 18000, "Asia/Ashgabat", "Asia/Ashkhabad" as any, "Asia/Dushanbe" as any, "Asia/Samarkand" as any, "Asia/Tashkent" as any, "Etc/GMT-5" as any, "Indian/Kerguelen" as any, "Indian/Maldives" as any, "PLT" as any);
  addZone("Asia/Yekaterinburg", 18000, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "Asia/Aqtobe");
  addZone("Asia/Bishkek", 18000, 3600, 2, -1, 1, 9000, 0, 9, -1, 1, 9000, 0);
  addZone("Asia/Calcutta", 19800, "IST");
  addZone("Asia/Katmandu", 20700);
  addZone("Asia/Dacca", 21600, "Antarctica/Mawson", "Antarctica/Vostok" as any, "Asia/Colombo" as any, "Asia/Dhaka" as any, "Asia/Thimbu" as any, "Asia/Thimphu" as any, "BST" as any, "Etc/GMT-6" as any, "Indian/Chagos" as any);
  addZone("Asia/Almaty", 21600, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "Asia/Novosibirsk", "Asia/Omsk", "Asia/Qyzylorda");
  addZone("Asia/Rangoon", 23400, "Indian/Cocos");
  addZone("Asia/Saigon", 25200, "Antarctica/Davis", "Asia/Bangkok" as any, "Asia/Hovd" as any, "Asia/Jakarta" as any, "Asia/Phnom_Penh" as any, "Asia/Pontianak" as any, "Asia/Vientiane" as any, "Etc/GMT-7" as any, "Indian/Christmas" as any, "VST" as any);
  addZone("Asia/Krasnoyarsk", 25200, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1);
  addZone("Asia/Hong_Kong", 28800, "Antarctica/Casey", "Asia/Brunei" as any, "Asia/Chongqing" as any, "Asia/Chungking" as any, "Asia/Harbin" as any, "Asia/Kashgar" as any, "Asia/Kuala_Lumpur" as any, "Asia/Kuching" as any, "Asia/Macao" as any, "Asia/Macau" as any, "Asia/Makassar" as any, "Asia/Manila" as any, "Asia/Shanghai" as any, "Asia/Singapore" as any, "Asia/Taipei" as any, "Asia/Ujung_Pandang" as any, "Asia/Ulaanbaatar" as any, "Asia/Ulan_Bator" as any, "Asia/Urumqi" as any, "Australia/Perth" as any, "Australia/West" as any, "CTT" as any, "Etc/GMT-8" as any, "Hongkong" as any, "PRC" as any, "Singapore" as any);
  addZone("Asia/Irkutsk", 28800, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1);
  addZone("Asia/Tokyo", 32400, "Asia/Choibalsan", "Asia/Dili" as any, "Asia/Jayapura" as any, "Asia/Pyongyang" as any, "Asia/Seoul" as any, "Etc/GMT-9" as any, "Japan" as any, "JST" as any, "Pacific/Palau" as any, "ROK" as any);
  addZone("Asia/Yakutsk", 32400, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1);
  addZone("Australia/North", 34200, "Australia/Darwin", "ACT" as any);
  addZone("Australia/Adelaide", 34200, 3600, 9, -1, 1, 7200, 1, 2, -1, 1, 7200, 1, "Australia/Broken_Hill", "Australia/South", "Australia/Yancowinna");
  addZone("Australia/Brisbane", 36000, "Antarctica/DumontDUrville", "Australia/Lindeman" as any, "Australia/Queensland" as any, "Etc/GMT-10" as any, "Pacific/Guam" as any, "Pacific/Port_Moresby" as any, "Pacific/Saipan" as any, "Pacific/Truk" as any, "Pacific/Yap" as any);
  addZone("Asia/Vladivostok", 36000, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "Asia/Sakhalin");
  addZone("Australia/Sydney", 36000, 3600, 9, -1, 1, 7200, 1, 2, -1, 1, 7200, 1, "AET", "Australia/ACT", "Australia/Canberra", "Australia/Melbourne", "Australia/NSW", "Australia/Victoria");
  addZone("Australia/Hobart", 36000, 3600, 9, 1, 1, 7200, 1, 2, -1, 1, 7200, 1, "Australia/Tasmania");
  addZone("Australia/Lord_Howe", 37800, 1800, 9, -1, 1, 7200, 0, 2, -1, 1, 7200, 0, "Australia/LHI");
  addZone("Pacific/Guadalcanal", 39600, "Etc/GMT-11", "Pacific/Efate" as any, "Pacific/Kosrae" as any, "Pacific/Noumea" as any, "Pacific/Ponape" as any, "SST" as any);
  addZone("Asia/Magadan", 39600, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1);
  addZone("Pacific/Norfolk", 41400);
  addZone("Pacific/Wake", 43200, "Etc/GMT-12", "Kwajalein" as any, "Pacific/Fiji" as any, "Pacific/Funafuti" as any, "Pacific/Kwajalein" as any, "Pacific/Majuro" as any, "Pacific/Nauru" as any, "Pacific/Tarawa" as any, "Pacific/Wallis" as any);
  addZone("Asia/Kamchatka", 43200, 3600, 2, -1, 1, 7200, 1, 9, -1, 1, 7200, 1, "Asia/Anadyr");
  addZone("Pacific/Auckland", 43200, 3600, 9, 1, 1, 7200, 1, 2, 15, 1, 7200, 1, "Antarctica/McMurdo", "Antarctica/South_Pole", "NST", "NZ");
  addZone("Pacific/Chatham", 45900, 3600, 9, 1, 1, 9900, 1, 2, 15, 1, 9900, 1, "NZ-CHAT");
  addZone("Pacific/Tongatapu", 46800, "Etc/GMT-13", "Pacific/Enderbury" as any);
  addZone("Pacific/Kiritimati", 50400, "Etc/GMT-14");

  // Sync to globalThis
  _global.ZONES = ZONES;
}

/**
 * Reads the IANA time zone the browser reports, e.g. "America/New_York".
 * @returns the id, or an empty string when the browser does not report one
 */
function intlTimeZoneId(): string {
  try {
    if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
      return "";
    }
    const resolved = Intl.DateTimeFormat().resolvedOptions();
    return (resolved && resolved.timeZone) ? resolved.timeZone : "";
  } catch (e) {
    return "";
  }
}

/**
 * Guesses the browser's timezone.
 *
 * Prefers the IANA id the browser reports, resolved through the alias table to the
 * canonical id the server stores — but only while that mapping still agrees with the
 * offsets the browser reports. Otherwise falls back to matching on UTC offset and
 * hemisphere, which is also what answers when the browser reports nothing or reports a
 * zone this table predates: that match is only as precise as (standard offset,
 * hemisphere), so zones sharing both collide and the earliest one added wins.
 *
 * @returns Timezone identifier or empty string if not found
 */
function guessTimeZone(): string {
  if (ZONES == null) {
    initZones();
  }

  const d1 = new Date();
  const d2 = new Date();
  d1.setMonth(5);  // June
  d2.setMonth(11); // December

  const o1 = -d1.getTimezoneOffset();
  const o2 = -d2.getTimezoneOffset();

  let hemisphere: string;
  if (o1 === o2) {
    hemisphere = "-";
  } else {
    hemisphere = (o1 > o2) ? "N" : "S";
  }

  const offset = ((hemisphere === "N") ? o2 : o1);

  // Intl names the zone exactly, but the table it has to be resolved against froze years ago and
  // maps most ids as aliases onto a canonical group. Where a zone's rules later diverged from its
  // group the alias answers with the group's rules and is an hour or more out — America/Mexico_City
  // still aliases onto US/Central though Mexico dropped DST in 2022, and Pacific/Apia onto US/Samoa
  // though Samoa crossed the dateline in 2011. So take the exact match only while the group still
  // describes what the browser reports; otherwise the scan below is the more trustworthy answer,
  // because it is derived from the browser rather than from the table.
  //
  // Returning the raw IANA id instead is not an option: the server's timezones option list is
  // exactly this canonical set, so a non-canonical value renders an unselected control.
  const intlZone = intlTimeZoneId();
  const named = intlZone === "" ? null : ZONES![intlZone];
  if (named != null && named.hemisphere === hemisphere && named.offset === offset) {
    return named.id;
  }

  for (const i in ZONES) {
    if (i === ZONES[i].id && ZONES[i].hemisphere === hemisphere && ZONES[i].offset === offset) {
      return ZONES[i].id;
    }
  }
  return "";
}

/**
 * Sets a cookie with the guessed timezone.
 * Requires cookie.js to be loaded.
 */
function setTimeZoneCookie(): void {
  const setCookie = _global.setCookie as ((name: string, value: string, expires?: Date | null, path?: string, domain?: string, secure?: boolean) => void) | undefined;
  if (typeof setCookie !== 'function') {
    return;
  }
  const zone = guessTimeZone();
  if (zone === "") {
    // A failed guess must not be cached. The server only injects this script when
    // the cookie is absent, so writing an empty value would suppress detection for
    // the life of the cookie and leave the user on the server-side fallback zone.
    // Leaving it unset costs nothing: the next page render retries.
    return;
  }
  const d = new Date();
  const y = d.getFullYear();
  d.setFullYear(y + 1);
  setCookie("timeZone", zone, d, undefined, undefined, undefined);
}

// Get reference to ZONES from globalThis if already initialized
if (_global.ZONES) {
  ZONES = _global.ZONES as Record<string, Zone>;
}

// Auto-set timezone cookie if cookie.js is loaded
if (typeof _COOKIE_JS_ !== 'undefined' && _COOKIE_JS_ === true) {
  setTimeZoneCookie();
}

// Export for module usage
export {
  Zone,
  ZoneRule,
  createZone,
  createZoneRule,
  addZone,
  initZones,
  intlTimeZoneId,
  guessTimeZone,
  setTimeZoneCookie,
  ZONES
};

// Expose globally for backwards compatibility
_global._TIMEZONETOOLS_JS_ = _TIMEZONETOOLS_JS_;
_global.initZones = initZones;
_global.guessTimeZone = guessTimeZone;
_global.setTimeZoneCookie = setTimeZoneCookie;

// Script resource notification
if (typeof _SR_ !== 'undefined' && _SR_ !== null) {
  _SR_.notify("timezonetools.js");
}
